import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { PlaybackState, TurnType } from '../../types/enums';
import type { Turn } from '../../types/ski-data';
import { usePlaybackMachine } from '../../state-machines/usePlaybackMachine';

/**
 * Timeline - Bottom 72px of R2 (full width).
 *
 * Shows the run timeline with:
 *   - Turn intervals as colored blocks (skidding=bad, edging=ok, carving=good).
 *   - A draggable ice-colored cursor with a "current / total" time label.
 *   - Turn numbers aligned to interval centers.
 *   - A data-coverage row (green where turns exist, gray for gaps, % on right).
 *   - Playback controls: prev turn, play/pause, next turn.
 *   - Speed controls: 1.0× and 0.5× (one-time tooltip on 0.5×).
 */
interface TimelineProps {
  turns: Turn[];
  durationMs: number;
}

const TYPE_COLORS: Record<TurnType, string> = {
  [TurnType.Skidding]: 'rgba(239, 68, 68, 0.45)',
  [TurnType.Edging]: 'rgba(245, 158, 11, 0.45)',
  [TurnType.Carving]: 'rgba(16, 185, 129, 0.45)',
};

export default function Timeline({ turns, durationMs }: TimelineProps) {
  const playback = usePlaybackMachine();
  const { currentTimestamp, state: pbState, speedMultiplier } = playback.state;
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [speedHintDismissed, setSpeedHintDismissed] = useState(false);

  const isPlaying = pbState === PlaybackState.Playing;

  // Map a client X to a timestamp and seek.
  const seekFromClientX = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || durationMs <= 0) return;
      const rect = el.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      playback.seek(ratio * durationMs);
    },
    [durationMs, playback],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      seekFromClientX(e.clientX);
    },
    [seekFromClientX],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      seekFromClientX(e.clientX);
    },
    [seekFromClientX],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = false;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // pointer may already be released
      }
    },
    [],
  );

  const cursorPct = durationMs > 0 ? (currentTimestamp / durationMs) * 100 : 0;
  const currentSec = (currentTimestamp / 1000).toFixed(1);
  const totalSec = (durationMs / 1000).toFixed(1);

  // Coverage: total turn duration / total duration.
  const turnMs = turns.reduce((sum, t) => sum + (t.end_ms - t.start_ms), 0);
  const coveragePct = durationMs > 0 ? Math.round((turnMs / durationMs) * 100) : 0;

  return (
    <div
      className="r2-timeline"
      style={{
        height: '72px',
        flexShrink: 0,
        padding: '4px var(--space-md)',
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--bg-elevated)',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      {/* Track: turn blocks + cursor + time label */}
      <div
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative',
          height: '28px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Turn blocks */}
        {turns.map((turn) => {
          const left = durationMs > 0 ? (turn.start_ms / durationMs) * 100 : 0;
          const width =
            durationMs > 0 ? ((turn.end_ms - turn.start_ms) / durationMs) * 100 : 0;
          return (
            <div
              key={turn.turn_index}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                top: '8px',
                height: '16px',
                background: TYPE_COLORS[turn.turn_type],
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-secondary)',
                overflow: 'hidden',
              }}
            >
              {width > 4 ? turn.turn_index : ''}
            </div>
          );
        })}

        {/* Cursor */}
        <div
          style={{
            position: 'absolute',
            left: `${cursorPct}%`,
            top: 0,
            bottom: 0,
            width: '2px',
            background: 'var(--color-ice)',
            transform: 'translateX(-1px)',
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-glow-ice)',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              left: '4px',
              fontSize: 'var(--fs-xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-ice)',
              whiteSpace: 'nowrap',
            }}
          >
            {currentSec}s / {totalSec}s
          </span>
        </div>
      </div>

      {/* Coverage row */}
      <div
        style={{
          position: 'relative',
          height: '6px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            flex: 1,
            height: '4px',
            background: 'var(--bg-surface-1)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          {turns.map((turn) => {
            const left = durationMs > 0 ? (turn.start_ms / durationMs) * 100 : 0;
            const width =
              durationMs > 0 ? ((turn.end_ms - turn.start_ms) / durationMs) * 100 : 0;
            return (
              <div
                key={turn.turn_index}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  top: 0,
                  bottom: 0,
                  background: 'var(--status-good)',
                  opacity: 0.6,
                }}
              />
            );
          })}
        </div>
        <span
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
            marginLeft: 'var(--space-sm)',
            flexShrink: 0,
          }}
        >
          {coveragePct}%
        </span>
      </div>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-md)',
          flex: 1,
        }}
      >
        <ControlButton onClick={() => playback.jumpTurn(-1)} title="上一弯">
          ⏮
        </ControlButton>
        <ControlButton
          onClick={() => (isPlaying ? playback.pause() : playback.play())}
          title={isPlaying ? '暂停' : '播放'}
          primary
        >
          {isPlaying ? '⏸' : '▶'}
        </ControlButton>
        <ControlButton onClick={() => playback.jumpTurn(1)} title="下一弯">
          ⏭
        </ControlButton>

        <div style={{ display: 'flex', gap: '4px', marginLeft: 'var(--space-sm)' }}>
          <SpeedButton
            active={speedMultiplier === 1.0}
            onClick={() => playback.setSpeed(1.0)}
          >
            1.0×
          </SpeedButton>
          <div style={{ position: 'relative' }}>
            <SpeedButton
              active={speedMultiplier === 0.5}
              onClick={() => {
                playback.setSpeed(0.5);
                setSpeedHintDismissed(true);
              }}
            >
              0.5×
            </SpeedButton>
            {!speedHintDismissed && (
              <span
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  marginBottom: '4px',
                  padding: '2px 6px',
                  background: 'var(--color-ice)',
                  color: 'var(--bg-base)',
                  fontSize: 'var(--fs-xs)',
                  borderRadius: 'var(--radius-sm)',
                  whiteSpace: 'nowrap',
                  fontWeight: 'var(--fw-semibold)',
                  pointerEvents: 'none',
                }}
              >
                慢速回放可观察压力转移细节
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Internal: control buttons =====

function ControlButton({
  children,
  onClick,
  title,
  primary,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  primary?: boolean;
}) {
  return (
    <button
      className="btn-hover"
      onClick={onClick}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: primary ? 32 : 28,
        height: 28,
        borderRadius: 'var(--radius-sm)',
        background: primary ? 'var(--color-ice)' : 'var(--bg-surface-1)',
        color: primary ? 'var(--bg-base)' : 'var(--text-secondary)',
        fontSize: 'var(--fs-sm)',
        border: '1px solid var(--border-default)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function SpeedButton({
  children,
  onClick,
  active,
}: {
  children: ReactNode;
  onClick: () => void;
  active: boolean;
}) {
  return (
    <button
      className="btn-hover"
      onClick={onClick}
      style={{
        padding: '2px 8px',
        height: 28,
        borderRadius: 'var(--radius-sm)',
        background: active ? 'var(--color-ice)' : 'var(--bg-surface-1)',
        color: active ? 'var(--bg-base)' : 'var(--text-secondary)',
        fontSize: 'var(--fs-xs)',
        fontFamily: 'var(--font-mono)',
        border: '1px solid var(--border-default)',
        cursor: 'pointer',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-regular)',
      }}
    >
      {children}
    </button>
  );
}
